import React, { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { hrDocumentApi } from '../../services/hrDocumentApi';
import { VerifyResult } from '../../services/hrDocumentApi';

interface LocationState extends VerifyResult {
  birthdate: string;
}

export default function DocumentSelectPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [selectedDoc, setSelectedDoc] = useState('');
  const [address, setAddress] = useState('');
  const [purpose, setPurpose] = useState('');
  const [recipientEmail, setRecipientEmail] = useState(state?.email || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // 초기 귀속연도: 입사일~퇴사일(또는 전년도) 범위 내 가장 최근 연도
  const [year, setYear] = useState(() => {
    const lastYear = state?.endDate
      ? new Date(state.endDate).getFullYear()
      : new Date().getFullYear() - 1;
    return lastYear;
  });
  const [submissionOrg, setSubmissionOrg] = useState('금융기관');
  const [submissionPurpose, setSubmissionPurpose] = useState('제출용');

  // 직접 접근 방지
  if (!state?.employeeId) {
    return <Navigate to="/document" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDoc) {
      setError('발급할 문서를 선택해주세요.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      await hrDocumentApi.issue({
        employeeId: state.employeeId,
        name: state.name,
        birthdate: state.birthdate,
        documentType: selectedDoc,
        address: selectedDoc !== '원천징수영수증' ? (address.trim() || undefined) : undefined,
        purpose: selectedDoc === '원천징수영수증' ? `${year}년 원천징수영수증 발급` : purpose.trim(),
        recipientEmail: recipientEmail.trim(),
        ...(selectedDoc === '원천징수영수증' ? { year, submissionOrg, submissionPurpose } : {}),
      });
      navigate('/document/complete', {
        state: { documentType: selectedDoc, recipientEmail: recipientEmail.trim() },
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setError(msg || '발급 요청 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const docTypeIcon: Record<string, string> = {
    '재직증명서': '🏢',
    '퇴직증명서': '📋',
    '경력증명서': '📄',
    '원천징수영수증': '🧾',
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-xl font-bold text-slate-800">GRK Career Management</h1>
          <p className="text-sm text-slate-500 mt-1">증명서 발급 포털</p>
        </div>

        <div className="bg-white rounded-xl shadow border border-slate-200 p-6">
          <p className="text-sm font-medium text-slate-800 mb-1">
            <span className="text-indigo-600">{state.name}</span>님,
          </p>
          <p className="text-xs text-slate-500 mb-5">발급할 문서를 선택하고 정보를 입력해주세요.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 문서 선택 */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">문서 종류 *</label>
              <div className={`grid gap-2 ${state.availableDocuments.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {state.availableDocuments.map((doc) => (
                  <button
                    key={doc}
                    type="button"
                    onClick={() => setSelectedDoc(doc)}
                    className={`flex flex-col items-center p-3 rounded-xl border-2 text-sm font-medium transition-colors ${
                      selectedDoc === doc
                        ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <span className="text-xl mb-1">{docTypeIcon[doc] ?? '📃'}</span>
                    <span className="text-xs">{doc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* 귀속연도 (원천징수영수증 선택 시) — 입사일~퇴사일(또는 전년도) 기준 */}
            {selectedDoc === '원천징수영수증' && (() => {
              const joinYear = state.joinDate ? new Date(state.joinDate).getFullYear() : new Date().getFullYear() - 5;
              const lastYear = state.endDate
                ? new Date(state.endDate).getFullYear()
                : new Date().getFullYear() - 1;
              const years: number[] = [];
              for (let y = lastYear; y >= joinYear; y--) {
                years.push(y);
              }
              return (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">귀속연도 *</label>
                  <select
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    {years.map((y) => (
                      <option key={y} value={y}>{y}년</option>
                    ))}
                  </select>
                </div>
              );
            })()}

            {/* 제출기관 (원천징수영수증 선택 시) */}
            {selectedDoc === '원천징수영수증' && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">제출기관 *</label>
                <select
                  value={submissionOrg}
                  onChange={(e) => setSubmissionOrg(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="금융기관">금융기관</option>
                  <option value="관공서">관공서</option>
                  <option value="조합">조합</option>
                  <option value="거래처">거래처</option>
                  <option value="학교">학교</option>
                  <option value="기타">기타</option>
                </select>
              </div>
            )}

            {/* 제출목적 (원천징수영수증 선택 시) */}
            {selectedDoc === '원천징수영수증' && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">제출목적 *</label>
                <select
                  value={submissionPurpose}
                  onChange={(e) => setSubmissionPurpose(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="제출용">제출용</option>
                  <option value="대출용">대출용</option>
                  <option value="기타">기타</option>
                </select>
              </div>
            )}

            {/* 주소 (선택) - 원천징수영수증에서는 불필요 */}
            {selectedDoc !== '원천징수영수증' && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  주소 <span className="text-slate-400 font-normal">(선택)</span>
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="서울특별시 용산구 ..."
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            )}

            {/* 용도 - 원천징수영수증에서는 불필요 */}
            {selectedDoc !== '원천징수영수증' && (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">용도 *</label>
                <input
                  type="text"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="은행 제출용"
                  required
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            )}

            {/* 수신 이메일
                - 재직(ACTIVE) 중: 회사 이메일 고정 (변경 불가)
                - 퇴사(RESIGNED): 본인이 수신 가능한 이메일로 입력/변경 가능 */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">수신 이메일 *</label>
              {state.status === 'ACTIVE' && state.email ? (
                <>
                  <input
                    type="email"
                    value={recipientEmail}
                    readOnly
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 text-slate-600 cursor-not-allowed"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">재직자는 등록된 회사 이메일로만 발송됩니다.</p>
                </>
              ) : (
                <>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder={state.email || 'hong@example.com'}
                    required
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <p className="text-[11px] text-slate-400 mt-1">퇴사자는 본인이 수신 가능한 이메일로 변경하여 발급받을 수 있습니다.</p>
                </>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !selectedDoc || (selectedDoc !== '원천징수영수증' && !purpose.trim()) || !recipientEmail.trim()}
              className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {loading ? '발급 요청 중...' : '발급 요청'}
            </button>
          </form>
        </div>

        <button
          onClick={() => navigate('/document')}
          className="w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-3 py-2"
        >
          ← 처음으로
        </button>
      </div>
    </div>
  );
}
